<template>
  <div
    ref="container"
    class="chat-thread"
  >
    <div
      v-if="messages.length === 0"
      class="chat-thread__empty"
    >
      <v-icon
        color="grey lighten-1"
        size="32"
      >
        mdi-chat-outline
      </v-icon>
      <span>No messages yet.</span>
    </div>
    <div
      v-for="message in messages"
      :key="message.id"
      class="chat-thread__message"
      :class="{
        'chat-thread__message--own': isOwnMessage(message),
        'chat-thread__message--mod': message.is_from_moderator && !isOwnMessage(message),
      }"
    >
      <div
        class="chat-thread__avatar"
        :class="{ 'chat-thread__avatar--mod': message.is_from_moderator }"
      >
        {{ getInitial(message.sender) }}
      </div>
      <div class="chat-thread__bubble">
        <div class="chat-thread__header">
          <span
            class="chat-thread__sender"
            :class="{ 'chat-thread__sender--mod': message.is_from_moderator }"
          >
            {{ getDisplayName(message) }}
          </span>
          <span
            v-if="getSenderRole(message)"
            class="chat-thread__role-badge"
            :class="`chat-thread__role-badge--${getSenderRole(message).toLowerCase()}`"
          >
            {{ getSenderRole(message) }}
          </span>
          <span class="chat-thread__time">{{ formatChatTimestamp(message.created_at) }}</span>
        </div>
        <div class="chat-thread__content">
          {{ message.content }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatChatTimestamp } from '@/helpers/formatting';

export default {
  name: 'ChatThread',

  props: {
    messages: {
      type: Array,
      required: true,
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    // { [userId]: 'Host' | 'Moderator' | 'Panelist' } — passed only for organizer chat
    roleMap: {
      type: Object,
      default: () => ({}),
    },
  },

  watch: {
    messages() {
      this.$nextTick(this.scrollToBottom);
    },
  },

  mounted() {
    this.scrollToBottom();
  },

  methods: {
    formatChatTimestamp,

    scrollToBottom() {
      const el = this.$refs.container;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    getInitial(sender) {
      return sender?.name?.charAt(0)?.toUpperCase() || '?';
    },

    getDisplayName(message) {
      const name = message.sender?.name || '';
      if (message.is_from_moderator) {
        return name.split(' ')[0];
      }
      return name;
    },

    isOwnMessage(message) {
      if (!this.currentUserId || !message.sender?.id) return false;
      // Use loose equality to handle string/number type mismatch between Django and API
      // eslint-disable-next-line eqeqeq
      return message.sender.id == this.currentUserId;
    },

    // Returns the role label ('Host', 'Moderator', 'Panelist') if roleMap is provided,
    // otherwise falls back to 'Mod' for is_from_moderator in non-organizer chats
    getSenderRole(message) {
      const senderId = message.sender?.id;
      if (senderId && this.roleMap && this.roleMap[senderId]) {
        return this.roleMap[senderId];
      }
      // Fallback for private chat where no roleMap is passed
      if (message.is_from_moderator) {
        return 'Mod';
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-thread {
  flex: 1;
  overflow-y: auto;
  padding: 10px;

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: rgba(0, 0, 0, 0.35);
    font-size: 13px;
    text-align: center;
    padding: 28px 0;
  }

  &__message {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
    animation: chatFadeIn 0.2s ease;

    &--own {
      flex-direction: row-reverse;

      .chat-thread__bubble {
        background: #f4f5f7;
        border-radius: 12px 2px 12px 12px;
      }

      .chat-thread__avatar {
        background: #e0e0e0;
        color: rgba(0, 0, 0, 0.55);
      }
    }

    &--mod {
      .chat-thread__bubble {
        background: #fef1e8;
        border-left: 2px solid #f47721;
      }
    }
  }

  &__avatar {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    background: #f47721;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    margin-top: 2px;

    &--mod {
      background: #f47721;
      color: #fff;
    }
  }

  &__bubble {
    padding: 8px 12px;
    border-radius: 2px 12px 12px 12px;
    background: #fef1e8;
    max-width: 85%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  &__header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 3px;
  }

  &__sender {
    font-size: 11px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);

    &--mod {
      color: #f47721;
    }
  }

  // Role badges — shown in organizer chat next to sender name
  &__role-badge {
    display: inline-block;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    color: #fff;
    border-radius: 3px;
    padding: 1px 4px;
    line-height: 1.3;
    letter-spacing: 0.03em;

    // Host — purple
    &--host {
      background: #7c3aed;
    }

    // Moderator — keep brand orange
    &--moderator {
      background: #f47721;
    }

    // Panelist — teal/cyan
    &--panelist {
      background: #0891b2;
    }

    // Private chat fallback badge "Mod"
    &--mod {
      background: #f47721;
    }
  }

  &__content {
    font-size: 13px;
    line-height: 1.45;
    word-break: break-word;
    color: rgba(0, 0, 0, 0.85);
  }

  &__time {
    font-size: 10px;
    color: rgba(0, 0, 0, 0.35);
    white-space: nowrap;
  }
}

@keyframes chatFadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
